import React, { useState, useEffect } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import TimelineOutlinedIcon from "@material-ui/icons/TimelineOutlined";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import TimerOutlinedIcon from "@material-ui/icons/TimerOutlined";
import {
	PieChart,
	Pie,
	Cell,
	BarChart,
	Bar,
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
} from "recharts";

import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import useUsers from "../../../hooks/useUsers";

// Backoffice - Visao Geral (admin/gestor). SOMENTE apresentacao: todo numero
// exibido aqui vem pronto de GET /solicitacoes/indicadores (agregado no
// backend, que reaproveita o mesmo calculateSla() usado no resto do modulo -
// ver docs/api/endpoints.md). Este componente NUNCA soma/calcula SLA
// localmente, so formata o que ja chegou calculado. As somas feitas aqui
// (ex.: total de um grafico para calcular percentual de legenda) sao
// aritmetica de apresentacao sobre numeros ja prontos - nao recalculam SLA
// nem duplicam a classificacao normal/atencao/critico/vencido.
const VALID_STATUSES = [
	"created",
	"in_progress",
	"waiting_btg",
	"waiting_assessor_client",
	"btg_refused",
	"completed",
	"cancelled",
];

// Altura do card de "Distribuicao por demanda" cresce com o numero de
// categorias em vez de Top N/scroll interno - ver analise no relatorio da
// etapa (secao 8): a pagina ja tem scroll vertical proprio (classes.root),
// entao deixar o card crescer e mais fiel ao dado real do que esconder
// categorias ou criar uma 2a rolagem aninhada. Constantes ajustadas na
// etapa de densidade (2a rodada) para reduzir sobra vazia por categoria.
const demandaChartHeight = count => Math.max(170, 44 + count * 28);

// Altura do painel do card "Analise operacional". Fixa e igual para as 2
// abas de proposito: variar por aba faria o card (e a linha inteira do
// grid) pular de altura a cada clique, que e pior do que sobrar um pouco
// de espaco no estado vazio da Evolucao.
const ANALISE_PANEL_HEIGHT = 220;

const formatPercent = (value, total) => {
	if (!total) return "0%";
	return `${(value / total * 100).toFixed(1).replace(".", ",")}%`;
};

// Minutos -> "14h 12m" / "45m". O backend devolve a media ja calculada em
// minutos (data.tma.minutos); aqui e so formatacao de apresentacao, para
// que o texto ("h"/"m") fique no frontend junto do resto do i18n.
const formatTma = minutos => {
	if (minutos === null || minutos === undefined) return null;
	const horas = Math.floor(minutos / 60);
	const min = minutos % 60;
	return horas > 0 ? `${horas}h ${min}m` : `${min}m`;
};

const useStyles = makeStyles(theme => ({
	// Causa raiz do corte vertical / scroll horizontal (achado na etapa
	// anterior): MainContainer.js (do proprio Whaticket, nao tocado) da ao
	// SEU filho direto um container com height:100% + overflowY:hidden,
	// esperando que ESSE filho seja um unico elemento flex:1 com overflow
	// proprio - exatamente o que MinhasSolicitacoes/index.js ja faz com a
	// tabela (classes.mainPaper). overflowX "hidden" (nao "auto") porque
	// este painel nunca deve precisar de scroll horizontal.
	root: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		overflowX: "hidden",
		padding: theme.spacing(0.5),
		...(theme.scrollbarStyles || {}),
	},
	filtersPaper: {
		padding: theme.spacing(1.25, 1.5),
		marginBottom: theme.spacing(1),
		borderRadius: 12,
	},
	// Campos "outlined" com canto arredondado no lugar do underline padrao
	// (variant standard) - o alvo visual do protótipo. O seletor precisa
	// alcancar o .MuiOutlinedInput-root de dentro do TextField, por isso vem
	// como classe no TextField e nao via InputProps de cada campo.
	roundedField: {
		"& .MuiOutlinedInput-root": {
			borderRadius: 8,
		},
	},
	// Grid de KPI: exatamente 5 colunas a partir de md (o grid do MUI e de 12
	// colunas, entao 5 iguais nao sai de xs/sm/md - vem de flexBasis 20%),
	// caindo para 3 em sm e 2 em xs.
	kpiGridItem: {
		[theme.breakpoints.up("md")]: {
			flexBasis: "20%",
			maxWidth: "20%",
		},
	},
	kpiPaper: {
		padding: theme.spacing(1.25, 1.5),
		height: "100%",
		borderRadius: 12,
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.5),
	},
	kpiLabel: {
		color: theme.palette.text.secondary,
		fontSize: "0.64rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.04em",
		lineHeight: 1.3,
	},
	kpiValue: {
		fontSize: "1.6rem",
		fontWeight: 700,
		lineHeight: 1.1,
		marginTop: "auto",
	},
	kpiHint: {
		color: theme.palette.text.secondary,
		fontSize: "0.66rem",
		lineHeight: 1.2,
	},
	// Badge do KPI de TMA. Mostra o tamanho da amostra, nao uma tendencia:
	// "-12% vs. semana anterior" exigiria uma janela de comparacao que o
	// endpoint nao define (o usuario filtra por periodo arbitrario), e um
	// numero de tendencia inventado e pior do que nenhum.
	kpiBadge: {
		alignSelf: "flex-start",
		display: "inline-flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		padding: theme.spacing(0.25, 0.75),
		borderRadius: 999,
		backgroundColor: theme.palette.action.hover,
		color: theme.palette.text.secondary,
		fontSize: "0.64rem",
		fontWeight: 600,
		lineHeight: 1.4,
	},
	kpiBadgeIcon: {
		fontSize: "0.85rem",
	},
	// Cards com ResponsiveContainer que precisa de height:100% resolvendo
	// contra um ancestral com altura DEFINIDA (barra/linha) - por isso
	// mantem altura fixa/computada em px, ao contrario de chartPaperAuto.
	chartPaperFixed: {
		padding: theme.spacing(0.75, 1.5),
		display: "flex",
		flexDirection: "column",
		borderRadius: 12,
	},
	// Cards de pizza/donut: o grafico fica numa caixa de tamanho FIXO em px
	// (chartCircleWrap), entao o Paper pode crescer com o conteudo
	// (minHeight, nao height) sem quebrar o ResponsiveContainer - permite a
	// legenda empilhar embaixo do grafico em telas estreitas sem cortar
	// nada (secao 14: "a pagina precisa poder crescer verticalmente").
	// minHeight aqui e so um piso para os estados vazios ("Sem dados para o
	// filtro selecionado") nao virarem um card raso demais. Com a legenda
	// agora EMBAIXO do donut (grade de 2 colunas) e nao mais ao lado, quem
	// manda na altura destes cards e o proprio conteudo, nao este valor.
	// height 100% para os 2 cards da mesma linha terminarem no mesmo ponto
	// (o Grid item ja e esticado pelo align-items:stretch; sem isso o Paper
	// so ocupava a altura do proprio conteudo e a linha ficava desalinhada).
	// So e seguro esticar porque o conteudo destes cards se centraliza
	// verticalmente (donutRow e slaBlock tem flex:1) - a sobra vira respiro
	// simetrico em cima e embaixo, nao um vao morto embaixo.
	chartPaperAuto: {
		padding: theme.spacing(0.75, 1.5),
		minHeight: 168,
		height: "100%",
		display: "flex",
		flexDirection: "column",
		borderRadius: 12,
	},
	chartTitle: {
		fontWeight: 600,
		fontSize: "0.82rem",
		marginBottom: theme.spacing(0.25),
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
	},
	chartTitleHelpIcon: {
		fontSize: "0.95rem",
		color: theme.palette.text.disabled,
		cursor: "help",
	},
	centeredState: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		color: theme.palette.text.secondary,
		textAlign: "center",
		padding: theme.spacing(1.5),
	},
	emptyStateIcon: {
		fontSize: 32,
		color: theme.palette.text.disabled,
		marginBottom: theme.spacing(0.5),
	},
	emptyStateTitle: {
		fontSize: "0.8rem",
		fontWeight: 600,
		color: theme.palette.text.secondary,
	},
	emptyStateCaption: {
		fontSize: "0.7rem",
		color: theme.palette.text.disabled,
		marginTop: theme.spacing(0.25),
		maxWidth: 260,
	},
	loadingRoot: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(4),
	},
	// Donut + legenda lateral - substitui labels externos com linha (que
	// sobrepunham entre si) por uma lista comum ao lado, que nunca sobrepoe
	// porque segue o fluxo normal do documento, nao posicionamento livre em
	// cima do SVG. Empilha em coluna abaixo de "sm" para nao espremer.
	// Donut centralizado no topo + legenda em grade de 2 colunas embaixo (no
	// lugar da legenda lateral): a legenda lateral deixava o donut encostado
	// na esquerda com sobra a direita, e uma grade de 2 colunas ocupa a
	// largura util do card. Continua sem label dentro do SVG com linha guia,
	// que era a causa original dos textos sobrepostos.
	donutRow: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: theme.spacing(1),
		minHeight: 0,
	},
	donutCircleWrap: {
		position: "relative",
		width: 128,
		height: 128,
		flexShrink: 0,
	},
	donutCenterLabel: {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		textAlign: "center",
		pointerEvents: "none",
	},
	donutCenterValue: {
		fontSize: "1.15rem",
		fontWeight: 700,
		lineHeight: 1,
	},
	donutCenterCaption: {
		fontSize: "0.6rem",
		color: theme.palette.text.secondary,
	},
	legendList: {
		width: "100%",
		minWidth: 0,
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		columnGap: theme.spacing(1.5),
		rowGap: theme.spacing(0.5),
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "minmax(0, 1fr)",
		},
	},
	legendRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
	},
	legendDot: {
		width: 8,
		height: 8,
		borderRadius: "50%",
		flexShrink: 0,
	},
	legendName: {
		flex: 1,
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		fontSize: "0.74rem",
	},
	legendValue: {
		fontWeight: 700,
		fontSize: "0.74rem",
		minWidth: 22,
		textAlign: "right",
	},
	legendPercent: {
		color: theme.palette.text.secondary,
		fontSize: "0.66rem",
		minWidth: 40,
		textAlign: "right",
	},
	// Barra proporcional empilhada de SLA - substitui o 2o donut. Eram duas
	// roscas visualmente identicas empilhadas na mesma coluna (repeticao sem
	// ganho de leitura), e uma barra empilhada compara a proporcao das 4
	// faixas de um mesmo total melhor do que uma rosca. Mesmos numeros de
	// data.sla, nenhuma reclassificacao nova: os 4 baldes
	// (normal/atencao/critico/vencido) chegam prontos do backend, que ja
	// reaproveita calculateSla() - este arquivo continua so apresentando.
	slaBlock: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		gap: theme.spacing(1.25),
		minHeight: 0,
	},
	slaTotalRow: {
		display: "flex",
		alignItems: "baseline",
		justifyContent: "space-between",
		gap: theme.spacing(1),
	},
	slaTotalValue: {
		fontSize: "1.35rem",
		fontWeight: 700,
		lineHeight: 1,
	},
	slaTotalCaption: {
		fontSize: "0.68rem",
		color: theme.palette.text.secondary,
		textAlign: "right",
	},
	// Segmentos usam flexGrow (nao width em %) para que os 2px de respiro
	// entre eles nao estourem a largura da faixa: o flexbox distribui o
	// espaco restante na proporcao dos valores, ja descontando os gaps.
	slaBarTrack: {
		display: "flex",
		gap: 2,
		height: 14,
		borderRadius: 7,
		overflow: "hidden",
		backgroundColor: theme.palette.action.hover,
		flexShrink: 0,
	},
	slaBarSegment: {
		height: "100%",
		flexBasis: 0,
		minWidth: 3,
	},
	slaCardsRow: {
		display: "flex",
		gap: theme.spacing(0.75),
		[theme.breakpoints.down("xs")]: {
			flexWrap: "wrap",
		},
	},
	slaCard: {
		flex: 1,
		minWidth: 84,
		padding: theme.spacing(0.75, 1),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius,
	},
	slaCardHead: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
	},
	slaCardLabel: {
		fontSize: "0.68rem",
		color: theme.palette.text.secondary,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	slaCardValue: {
		fontSize: "1.05rem",
		fontWeight: 700,
		lineHeight: 1.2,
	},
	slaCardPercent: {
		fontSize: "0.66rem",
		color: theme.palette.text.secondary,
	},
	slaCardDesc: {
		fontSize: "0.62rem",
		color: theme.palette.text.disabled,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},
	// Card "Analise operacional": titulo + abas na mesma linha do header,
	// painel de altura fixa abaixo. Funde "carga por atendente" (novo) e
	// "evolucao temporal" (que era um card solto) em um unico card, em vez
	// de os dois disputarem espaco na mesma linha.
	analiseHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		flexWrap: "wrap",
	},
	analiseTabsRoot: {
		minHeight: 30,
	},
	analiseTab: {
		minHeight: 30,
		minWidth: "auto",
		padding: theme.spacing(0.25, 1.25),
		fontSize: "0.7rem",
		fontWeight: 600,
		textTransform: "none",
	},
	analisePanel: {
		height: ANALISE_PANEL_HEIGHT,
		display: "flex",
		flexDirection: "column",
		marginTop: theme.spacing(0.5),
	},
}));

const EMPTY_FILTERS = {
	createdFrom: "",
	createdTo: "",
	status: "",
	typeId: "",
	attendantId: "",
};

// Donut compacto + legenda lateral (sem labels externos/linhas - unica
// causa dos textos sobrepostos identificada na analise desta etapa). Uso
// interno deste arquivo apenas (nao e reaproveitado em outro lugar do
// modulo), entao fica local em vez de um novo arquivo de componente.
const DonutWithLegend = ({ classes, items, centerValue, centerCaption }) => {
	const total = items.reduce((sum, item) => sum + item.quantidade, 0);
	return (
		<div className={classes.donutRow}>
			<div className={classes.donutCircleWrap}>
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={items}
							dataKey="quantidade"
							nameKey="nome"
							cx="50%"
							cy="50%"
							innerRadius={40}
							outerRadius={62}
							labelLine={false}
							label={false}
						>
							{items.map(entry => (
								<Cell key={entry.nome} fill={entry.cor} />
							))}
						</Pie>
						<RechartsTooltip
							formatter={(value, name) => [`${value} (${formatPercent(value, total)})`, name]}
						/>
					</PieChart>
				</ResponsiveContainer>
				<div className={classes.donutCenterLabel}>
					<Typography className={classes.donutCenterValue}>{centerValue}</Typography>
					<Typography className={classes.donutCenterCaption}>{centerCaption}</Typography>
				</div>
			</div>
			<div className={classes.legendList}>
				{items.map(item => (
					<div key={item.nome} className={classes.legendRow}>
						<span className={classes.legendDot} style={{ backgroundColor: item.cor }} />
						<span className={classes.legendName} title={item.nome}>{item.nome}</span>
						<span className={classes.legendValue}>{item.quantidade}</span>
						<span className={classes.legendPercent}>{formatPercent(item.quantidade, total)}</span>
					</div>
				))}
			</div>
		</div>
	);
};

const VisaoGeralPanel = () => {
	const classes = useStyles();
	const theme = useTheme();

	const [filters, setFilters] = useState(EMPTY_FILTERS);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [types, setTypes] = useState([]);
	const [analiseTab, setAnaliseTab] = useState("atendente");
	const { users } = useUsers();

	// Tipos de demanda para o filtro - mesma chamada ja usada no modal de
	// Nova Solicitacao (SolicitacaoModal), so 1 vez ao montar.
	useEffect(() => {
		const fetchTypes = async () => {
			try {
				const { data: typesData } = await api.get("/solicitacoes/tipos");
				setTypes(typesData);
			} catch (err) {
				toastError(err);
			}
		};
		fetchTypes();
	}, []);

	useEffect(() => {
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchIndicadores = async () => {
				try {
					const { data: indicadoresData } = await api.get(
						"/solicitacoes/indicadores",
						{
							params: {
								createdFrom: filters.createdFrom || undefined,
								createdTo: filters.createdTo || undefined,
								status: filters.status || undefined,
								typeId: filters.typeId || undefined,
								attendantId: filters.attendantId || undefined,
							},
						}
					);
					setData(indicadoresData);
					setError(null);
				} catch (err) {
					setError(err);
					toastError(err);
				} finally {
					setLoading(false);
				}
			};
			fetchIndicadores();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [filters]);

	const handleFilterChange = field => e => {
		setFilters(prev => ({ ...prev, [field]: e.target.value }));
	};

	const handleClearFilters = () => setFilters(EMPTY_FILTERS);

	if (loading && !data) {
		return (
			<div className={classes.loadingRoot}>
				<CircularProgress />
			</div>
		);
	}

	if (error && !data) {
		return (
			<div className={classes.centeredState}>
				<ErrorOutlineIcon fontSize="large" />
				<Typography variant="body1" style={{ marginTop: 8 }}>
					{i18n.t("minhasSolicitacoes.visaoGeral.errorState")}
				</Typography>
				<Button
					variant="outlined"
					color="primary"
					onClick={() => setFilters(prev => ({ ...prev }))}
					style={{ marginTop: 12 }}
				>
					{i18n.t("minhasSolicitacoes.visaoGeral.retry")}
				</Button>
			</div>
		);
	}

	// 5 KPIs: total, em andamento, aguardando (soma dos 2 status de espera),
	// concluidas e TMA. "Recusa BTG" saiu daqui para o layout de 5 colunas,
	// mas NAO saiu da tela: continua no donut "Distribuicao por status" (com
	// valor e percentual na legenda) e no filtro de status.
	const aguardandoTotal =
		data.por_status.aguardando_btg + data.por_status.aguardando_assessor_cliente;
	const tmaFormatado = formatTma(data.tma.minutos);
	const kpis = [
		{
			id: "total",
			label: i18n.t("minhasSolicitacoes.visaoGeral.kpis.total"),
			value: data.total,
			hint: i18n.t("minhasSolicitacoes.visaoGeral.kpis.hintPeriodo"),
		},
		{
			id: "em_andamento",
			label: i18n.t("minhasSolicitacoes.visaoGeral.kpis.emAndamento"),
			value: data.por_status.em_andamento,
			hint: `${formatPercent(data.por_status.em_andamento, data.total)} ${i18n.t("minhasSolicitacoes.visaoGeral.kpis.hintDoTotal")}`,
			cor: theme.palette.info.main,
		},
		{
			id: "aguardando",
			label: i18n.t("minhasSolicitacoes.visaoGeral.kpis.aguardando"),
			value: aguardandoTotal,
			hint: i18n.t("minhasSolicitacoes.visaoGeral.kpis.hintAguardando"),
			cor: theme.palette.warning.main,
		},
		{
			id: "concluidas",
			label: i18n.t("minhasSolicitacoes.visaoGeral.kpis.concluidas"),
			value: data.por_status.concluidas,
			hint: `${formatPercent(data.por_status.concluidas, data.total)} ${i18n.t("minhasSolicitacoes.visaoGeral.kpis.hintDoTotal")}`,
			cor: theme.palette.success.main,
		},
		{
			id: "tma",
			label: i18n.t("minhasSolicitacoes.visaoGeral.kpis.tma"),
			// data.tma.disponivel distingue "media = 0" de "nenhuma concluida no
			// filtro" - sem isso um periodo sem conclusao mostraria "0m".
			value: data.tma.disponivel
				? tmaFormatado
				: i18n.t("minhasSolicitacoes.visaoGeral.kpis.tmaVazio"),
			hint: data.tma.disponivel
				? null
				: i18n.t("minhasSolicitacoes.visaoGeral.kpis.hintTmaVazio"),
			badge: data.tma.disponivel
				? `${i18n.t("minhasSolicitacoes.visaoGeral.kpis.hintTmaAmostra")} ${data.tma.amostra} ${i18n.t("minhasSolicitacoes.visaoGeral.kpis.hintTmaConcluidas")}`
				: null,
		},
	];
	const statusChartData = [
		{ nome: i18n.t("minhasSolicitacoes.tabs.in_progress"), quantidade: data.por_status.em_andamento, cor: theme.palette.info.main },
		{ nome: i18n.t("minhasSolicitacoes.tabs.waiting_btg"), quantidade: data.por_status.aguardando_btg, cor: theme.palette.primary.main },
		{ nome: i18n.t("minhasSolicitacoes.tabs.waiting_assessor_client"), quantidade: data.por_status.aguardando_assessor_cliente, cor: "#5E35B1" },
		{ nome: i18n.t("minhasSolicitacoes.tabs.btg_refused"), quantidade: data.por_status.recusa_btg, cor: theme.palette.error.main },
		{ nome: i18n.t("minhasSolicitacoes.tabs.completed"), quantidade: data.por_status.concluidas, cor: theme.palette.success.main },
		{ nome: i18n.t("minhasSolicitacoes.tabs.cancelled"), quantidade: data.por_status.canceladas, cor: theme.palette.grey[500] },
	].filter(item => item.quantidade > 0);

	// Os 4 baldes de SLA na ordem de gravidade (a mesma de
	// situacaoFromPercentual no backend), sempre todos os 4 - inclusive os
	// zerados: "0 vencidas" tambem e informacao, e um balde que desaparece
	// quando zera faz o card mudar de forma a cada filtro. Quem filtra por
	// quantidade > 0 e apenas a barra (uma fatia de 0% nao e desenhavel).
	const slaSegments = [
		{ id: "normal", label: i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaNormal"), desc: i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaDesc.normal"), quantidade: data.sla.normal, cor: theme.palette.success.main },
		{ id: "atencao", label: i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaAtencao"), desc: i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaDesc.atencao"), quantidade: data.sla.atencao, cor: theme.palette.warning.main },
		{ id: "critico", label: i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaCritico"), desc: i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaDesc.critico"), quantidade: data.sla.critico, cor: theme.palette.error.main },
		{ id: "vencido", label: i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaVencido"), desc: i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaDesc.vencido"), quantidade: data.sla.vencido, cor: theme.palette.error.dark },
	];
	const slaAtivosTotal = slaSegments.reduce((sum, seg) => sum + seg.quantidade, 0);

	return (
		<div id="visao-geral-panel" className={classes.root}>
			<Paper className={classes.filtersPaper} variant="outlined">
				<Grid container spacing={1} alignItems="center">
					<Grid item xs={6} sm={2}>
						<TextField
							id="visao-geral-filtro-periodo-de"
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.periodoDe")}
							type="date"
							fullWidth
							size="small"
							variant="outlined"
							className={classes.roundedField}
							InputLabelProps={{ shrink: true }}
							value={filters.createdFrom}
							onChange={handleFilterChange("createdFrom")}
						/>
					</Grid>
					<Grid item xs={6} sm={2}>
						<TextField
							id="visao-geral-filtro-periodo-ate"
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.periodoAte")}
							type="date"
							fullWidth
							size="small"
							variant="outlined"
							className={classes.roundedField}
							InputLabelProps={{ shrink: true }}
							value={filters.createdTo}
							onChange={handleFilterChange("createdTo")}
						/>
					</Grid>
					<Grid item xs={12} sm={2}>
						<TextField
							id="visao-geral-filtro-status"
							select
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.status")}
							fullWidth
							size="small"
							variant="outlined"
							className={classes.roundedField}
							value={filters.status}
							onChange={handleFilterChange("status")}
						>
							<MenuItem value="">
								{i18n.t("minhasSolicitacoes.visaoGeral.filtros.statusTodos")}
							</MenuItem>
							{VALID_STATUSES.map(s => (
								<MenuItem key={s} value={s}>
									{i18n.t(`solicitacoes.status.${s}`)}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} sm={3}>
						<TextField
							id="visao-geral-filtro-demanda"
							select
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.demanda")}
							fullWidth
							size="small"
							variant="outlined"
							className={classes.roundedField}
							value={filters.typeId}
							onChange={handleFilterChange("typeId")}
						>
							<MenuItem value="">
								{i18n.t("minhasSolicitacoes.visaoGeral.filtros.demandaTodas")}
							</MenuItem>
							{types.map(t => (
								<MenuItem key={t.id} value={t.id}>
									{t.name}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} sm={2}>
						<TextField
							id="visao-geral-filtro-atendente"
							select
							label={i18n.t("minhasSolicitacoes.visaoGeral.filtros.atendente")}
							fullWidth
							size="small"
							variant="outlined"
							className={classes.roundedField}
							value={filters.attendantId}
							onChange={handleFilterChange("attendantId")}
						>
							<MenuItem value="">
								{i18n.t("minhasSolicitacoes.visaoGeral.filtros.atendenteTodos")}
							</MenuItem>
							{users.map(u => (
								<MenuItem key={u.id} value={String(u.id)}>
									{u.name}
								</MenuItem>
							))}
						</TextField>
					</Grid>
					<Grid item xs={12} sm={1}>
						<Button
							id="visao-geral-filtro-limpar"
							size="small"
							onClick={handleClearFilters}
						>
							{i18n.t("minhasSolicitacoes.visaoGeral.filtros.limpar")}
						</Button>
					</Grid>
				</Grid>
			</Paper>

			<Grid container spacing={1}>
				{kpis.map(kpi => (
					<Grid item xs={6} sm={4} className={classes.kpiGridItem} key={kpi.id}>
						<Paper
							id={`visao-geral-kpi-${kpi.id}`}
							className={classes.kpiPaper}
							variant="outlined"
						>
							<Typography className={classes.kpiLabel}>{kpi.label}</Typography>
							<Typography
								className={classes.kpiValue}
								style={kpi.cor ? { color: kpi.cor } : undefined}
							>
								{kpi.value}
							</Typography>
							{kpi.badge ? (
								<span id={`visao-geral-kpi-${kpi.id}-badge`} className={classes.kpiBadge}>
									<TimerOutlinedIcon className={classes.kpiBadgeIcon} />
									{kpi.badge}
								</span>
							) : (
								<Typography className={classes.kpiHint}>{kpi.hint}</Typography>
							)}
						</Paper>
					</Grid>
				))}
			</Grid>

			{/* SLA nao tem mais indicador nenhum aqui em cima (removido - Etapa
			    de refinamento): duplicava exatamente os mesmos 3 numeros que o
			    card "Situacao do SLA" abaixo ja mostra, com grafico e legenda.
			    Unica fonte de dados de SLA na tela agora e aquele card. */}

			{/* Proporcao 5:7 em vez de 6:6 - o donut de status precisa de bem
			    menos largura que o grafico de demanda, cujos rotulos de
			    categoria sao longos ("Atualizacao / Alteracao / Revisao
			    Cadastral") e disputavam espaco com as barras. O MUI v4 usa
			    grid de 12 colunas, entao 5/12 + 7/12 e o equivalente do 2:3. */}
			<Grid container spacing={1} style={{ marginTop: 12 }}>
				<Grid item xs={12} md={5}>
					<Paper id="visao-geral-grafico-status" className={classes.chartPaperAuto} variant="outlined">
						<Typography className={classes.chartTitle} variant="body2">
							{i18n.t("minhasSolicitacoes.visaoGeral.graficos.porStatusTitle")}
						</Typography>
						{statusChartData.length === 0 ? (
							<div className={classes.centeredState}>
								{i18n.t("minhasSolicitacoes.visaoGeral.graficos.semDados")}
							</div>
						) : (
							<DonutWithLegend
								classes={classes}
								items={statusChartData}
								centerValue={data.total}
								centerCaption={i18n.t("minhasSolicitacoes.visaoGeral.graficos.totalCurto")}
							/>
						)}
					</Paper>
				</Grid>

				<Grid item xs={12} md={7}>
					<Paper
						id="visao-geral-grafico-demanda"
						className={classes.chartPaperFixed}
						style={{ height: demandaChartHeight(data.por_demanda.length) }}
						variant="outlined"
					>
						<Typography className={classes.chartTitle} variant="body2">
							{i18n.t("minhasSolicitacoes.visaoGeral.graficos.porDemandaTitle")}
						</Typography>
						{data.por_demanda.length === 0 ? (
							<div className={classes.centeredState}>
								{i18n.t("minhasSolicitacoes.visaoGeral.graficos.semDados")}
							</div>
						) : (
							<ResponsiveContainer width="100%" height="100%">
								{/* Sem grade e sem eixo numerico visivel: o valor exato de
								    cada barra fica na ponta dela, entao a escala embaixo
								    so repetia a mesma informacao com mais tinta. O XAxis
								    continua existindo (hide) porque e ele quem define o
								    dominio contra o qual as barras sao dimensionadas -
								    remover a tag quebraria a proporcao. margin.right
								    reserva o espaco onde o rotulo da ponta e desenhado.
								    barCategoryGap em % e nao em px: a altura deste card vem
								    de demandaChartHeight (~28px por categoria), entao um gap
								    fixo de 12px consumia quase toda a faixa e deixava a
								    barra com 4px de espessura; em % o respiro acompanha a
								    faixa e a espessura fica estavel, limitada por
								    maxBarSize. */}
								<BarChart
									data={data.por_demanda}
									layout="vertical"
									margin={{ left: 8, right: 32, top: 4, bottom: 4 }}
									barCategoryGap="25%"
								>
									<XAxis type="number" allowDecimals={false} hide />
									<YAxis
										type="category"
										dataKey="nome"
										width={190}
										tickLine={false}
										axisLine={false}
										tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
									/>
									<RechartsTooltip formatter={value => [value, i18n.t("minhasSolicitacoes.visaoGeral.graficos.porDemandaTitle")]} />
									<Bar
										dataKey="quantidade"
										fill={theme.palette.primary.main}
										radius={[0, 6, 6, 0]}
										maxBarSize={18}
										label={{
											position: "right",
											fontSize: 11,
											fill: theme.palette.text.primary,
										}}
									/>
								</BarChart>
							</ResponsiveContainer>
						)}
					</Paper>
				</Grid>

				<Grid item xs={12} md={6}>
					<Paper id="visao-geral-grafico-sla" className={classes.chartPaperAuto} variant="outlined">
						<Typography className={classes.chartTitle} variant="body2">
							{i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaTitle")}
							<Tooltip
								title={`${slaAtivosTotal} ${i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaTooltip")}`}
							>
								<HelpOutlineIcon
									id="visao-geral-sla-tooltip-icon"
									className={classes.chartTitleHelpIcon}
									fontSize="inherit"
								/>
							</Tooltip>
						</Typography>
						{slaAtivosTotal === 0 ? (
							<div className={classes.centeredState}>
								{i18n.t("minhasSolicitacoes.visaoGeral.graficos.semDados")}
							</div>
						) : (
							<div className={classes.slaBlock}>
								<div className={classes.slaTotalRow}>
									<Typography className={classes.slaTotalValue}>
										{slaAtivosTotal}
									</Typography>
									<Typography className={classes.slaTotalCaption}>
										{i18n.t("minhasSolicitacoes.visaoGeral.graficos.slaAtivosLabel")}
									</Typography>
								</div>

								<div id="visao-geral-sla-barra" className={classes.slaBarTrack}>
									{slaSegments
										.filter(seg => seg.quantidade > 0)
										.map(seg => (
											<Tooltip
												key={seg.id}
												title={`${seg.label}: ${seg.quantidade} (${formatPercent(seg.quantidade, slaAtivosTotal)})`}
											>
												<div
													className={classes.slaBarSegment}
													style={{ backgroundColor: seg.cor, flexGrow: seg.quantidade }}
												/>
											</Tooltip>
										))}
								</div>

								<div className={classes.slaCardsRow}>
									{slaSegments.map(seg => (
										<div
											key={seg.id}
											id={`visao-geral-sla-card-${seg.id}`}
											className={classes.slaCard}
										>
											<div className={classes.slaCardHead}>
												<span
													className={classes.legendDot}
													style={{ backgroundColor: seg.cor }}
												/>
												<span className={classes.slaCardLabel} title={seg.label}>
													{seg.label}
												</span>
											</div>
											<Typography className={classes.slaCardValue} style={{ color: seg.cor }}>
												{seg.quantidade}
											</Typography>
											<Typography className={classes.slaCardPercent}>
												{formatPercent(seg.quantidade, slaAtivosTotal)}
											</Typography>
											<Typography className={classes.slaCardDesc} title={seg.desc}>
												{seg.desc}
											</Typography>
										</div>
									))}
								</div>
							</div>
						)}
					</Paper>
				</Grid>

				{/* "Analise operacional": funde a carga por atendente (dado novo,
				    data.por_atendente) e a evolucao temporal (que era um card
				    solto) em um card com abas. A aba padrao e a operacional ("o
				    que a equipe esta fazendo agora"); a temporal e a 2a pergunta.
				    A regra de "ha dados suficientes" para a evolucao continua 100%
				    no backend (dados_suficientes) - aqui so se reage a ela. */}
				<Grid item xs={12} md={6}>
					<Paper
						id="visao-geral-analise-operacional"
						className={classes.chartPaperAuto}
						variant="outlined"
					>
						<div className={classes.analiseHeader}>
							<Typography className={classes.chartTitle} variant="body2">
								{i18n.t("minhasSolicitacoes.visaoGeral.graficos.analiseTitle")}
							</Typography>
							<Tabs
								value={analiseTab}
								onChange={(e, value) => setAnaliseTab(value)}
								indicatorColor="primary"
								textColor="primary"
								classes={{ root: classes.analiseTabsRoot }}
							>
								<Tab
									id="visao-geral-aba-atendente"
									value="atendente"
									label={i18n.t("minhasSolicitacoes.visaoGeral.graficos.abaAtendente")}
									className={classes.analiseTab}
								/>
								<Tab
									id="visao-geral-aba-evolucao"
									value="evolucao"
									label={i18n.t("minhasSolicitacoes.visaoGeral.graficos.abaEvolucao")}
									className={classes.analiseTab}
								/>
							</Tabs>
						</div>

						<div className={classes.analisePanel}>
							{analiseTab === "atendente" &&
								(data.por_atendente.length === 0 ? (
									<div className={classes.centeredState}>
										{i18n.t("minhasSolicitacoes.visaoGeral.graficos.semDados")}
									</div>
								) : (
									<ResponsiveContainer width="100%" height="100%">
										{/* margin.top maior que o das outras margens para caber o
										    rotulo acima da barra mais alta sem corta-lo. */}
										<BarChart
											data={data.por_atendente}
											margin={{ top: 18, right: 8, left: -18, bottom: 0 }}
										>
											<CartesianGrid strokeDasharray="3 3" vertical={false} />
											<XAxis dataKey="nome" tick={{ fontSize: 11 }} interval={0} />
											<YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
											<RechartsTooltip
												formatter={value => [value, i18n.t("minhasSolicitacoes.visaoGeral.graficos.porAtendenteTooltip")]}
											/>
											{/* Valor no topo da barra: com carga muito desigual
											    (ex.: 94 x 1) a barra menor vira um risco no eixo e
											    o numero e a unica leitura possivel sem hover. */}
											<Bar
												dataKey="quantidade"
												fill={theme.palette.primary.main}
												radius={[6, 6, 0, 0]}
												maxBarSize={48}
												label={{
													position: "top",
													fontSize: 11,
													fill: theme.palette.text.primary,
												}}
											/>
										</BarChart>
									</ResponsiveContainer>
								))}

							{analiseTab === "evolucao" &&
								(!data.evolucao.dados_suficientes ? (
									<div id="visao-geral-evolucao-insuficiente" className={classes.centeredState}>
										<TimelineOutlinedIcon className={classes.emptyStateIcon} />
										<Typography className={classes.emptyStateTitle}>
											{i18n.t("minhasSolicitacoes.visaoGeral.graficos.evolucaoInsuficienteTitulo")}
										</Typography>
										<Typography className={classes.emptyStateCaption}>
											{i18n.t("minhasSolicitacoes.visaoGeral.graficos.evolucaoInsuficienteLegenda")}
										</Typography>
									</div>
								) : (
									<ResponsiveContainer width="100%" height="100%">
										<LineChart
											data={data.evolucao.dias}
											margin={{ top: 8, right: 8, left: -18, bottom: 0 }}
										>
											<CartesianGrid strokeDasharray="3 3" vertical={false} />
											<XAxis dataKey="dia" tick={{ fontSize: 11 }} />
											<YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
											<RechartsTooltip />
											<Line
												type="monotone"
												dataKey="quantidade"
												stroke={theme.palette.primary.main}
												strokeWidth={2}
											/>
										</LineChart>
									</ResponsiveContainer>
								))}
						</div>
					</Paper>
				</Grid>
			</Grid>
		</div>
	);
};

export default VisaoGeralPanel;
